import React, { useState } from 'react';
import { CalculatorInputs } from '../../types/calculator';
import { Autocomplete } from '../Autocomplete';
import { getSuggestedDosages } from '../../utils/storage/suggestedDosages';
import { SuggestedDosage } from '../../types/data';
import { generatePlanDescription } from '../../utils/plans';

interface LoadCalculateModalProps {
    closeModal: () => void;
    onInputChange: (updates: Partial<CalculatorInputs>) => void;
}

export const LoadCalculateModal: React.FC<LoadCalculateModalProps> = ({
    onInputChange,
    closeModal,
}) => {
    const [localValue, setLocalValue] = useState<string | null>(null); // Selected substance name
    const [records, setRecords] = useState<SuggestedDosage[]>([]); // Filtered records for the selected substance
    const [selectedRecord, setSelectedRecord] = useState<SuggestedDosage | null>(null); // User's selected record

    const handleSubstanceChange = (value: string) => {
        setLocalValue(value);
        const suggestedRecords = getSuggestedDosages().filter((s) => s.name === value);
        setRecords(suggestedRecords);
    };

    const handleConfirm = () => {
        if (selectedRecord) {
            // Map selected record data to CalculatorInputs and pass to parent
            const {
                dosage,
                dosage_units: dosageUnits,
                name: peptideName,
                syringe_units_per_dose: targetSyringeUnits,
                amount: vialAmount,
                amount_units: vialUnits,
            } = selectedRecord; // For suggested dosages

            onInputChange({
                dosage: dosage,
                dosageUnits: dosageUnits,
                peptideName: peptideName,
                targetSyringeUnits: targetSyringeUnits ?? 50,
                targetSyringeCapacity: 100,
                vialAmount: vialAmount,
                vialUnits: vialUnits,
            });

            closeModal(); // Close the modal
        }
    };

    const suggestedNames = Array.from(new Set(getSuggestedDosages().map(r => r.name)));

    return (
        <div>
            {/* Autocomplete for selecting a substance */}
            <Autocomplete
                label="Substance Name"
                options={suggestedNames}
                onChange={handleSubstanceChange}
                value={localValue || ''}
                tooltip="The name of the peptide, coenzyme, amino acid, etc."
                placeholder="Type to search..."
            />

            {/* Display records as a selectable list */}
            {records.length > 0 && (
                <div className="mt-4">
                    <h3 className="text-lg font-semibold mb-2">Select a Suggested Dosage</h3>
                    <div className="space-y-2">
                        {records.map((record, index) => (
                            <div key={index} className="flex items-center space-x-3">
                                <input
                                    type="radio"
                                    id={`record-${index}`}
                                    name="selected-record"
                                    value={index}
                                    onChange={() => setSelectedRecord(record)}
                                    checked={selectedRecord === record}
                                    className="w-4 h-4"
                                />
                                <label
                                    htmlFor={`record-${index}`}
                                    className="text-sm text-gray-700"
                                >
                                    {generatePlanDescription(record)}
                                </label>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Confirm and Cancel Buttons */}
            {records.length > 0 && selectedRecord && (
                <div className="mt-6 flex justify-center space-x-4">
                    <button
                        onClick={handleConfirm}
                        className="bg-indigo-500 hover:bg-indigo-600 text-white py-2 px-4 rounded"
                    >
                        Confirm
                    </button>
                    <button
                        onClick={closeModal}
                        className="bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded"
                    >
                        Cancel
                    </button>
                </div>
            )}

            {/* Disclaimer */}
            {records.length > 0 && (
                <p className="text-xs text-gray-600 mt-4">
                    <strong>Disclaimer:</strong> The information provided here is for sample purposes only and should not be considered as medical or professional advice. Consult a qualified professional before making any decisions. We are not responsible for any outcomes resulting from the use of this data.
                </p>
            )}
        </div>
    );
};
